import React, {
  useCallback, useMemo, useState, ReactElement,
} from 'react';
import Card from '../../../components/card/card.component';
import Table from '../../../components/table/table.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import Button from '../../../components/button/button.component';
import ModalButtonBlock from '../../../components/modal-button-block/modal-button-block.component';
import LeaveConfirmModal from '../../../components/leave-confirm-modal/leave-confirm-modal.component';
import RequiredMark from '../../../components/required-mark/required-mark.component';
import ModalLayout from '../../../components/modal-layout/modal-layout.component';
import Loader from '../../../components/loader/loader.component';
import FilteredLink from '../../../components/filtered-link/filtered-link.component';
import Form from '../../../components/form/form.component';
import FormFieldSelect from '../../../components/form-field-select/form-field-select.component';
import FormFieldText from '../../../components/form-field-text/form-field-text.component';
import FormFieldUpload from '../../../components/form-field-upload/form-field-upload.component';
import MenuItemList from '../../../components/menu-item-list/menu-item-list.component';
import ControlButtonBlock from '../../app/common/control-button-block/control-button-block.component';
import DownloadIcon from '../../../icons/download-icon';
import FormService from '../../../services/form-service';
import { DEAL_VALUATION_FILE_EDIT } from '../../../constants/entity/permissions';

const DealValuationFileModal: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const MODAL_TITLE_DEFAULT: string = 'New File';
  const MODAL_TITLE_EDIT_PREFIX: string = 'Edit';

  const {
    isOpen,
    isSaving,
    isSaveDisabled,
    isEditMode,
    isNewDeal,
    isModalEditMode,
    isRightDrawerOpen,
    isNewEntity,
    dealValuationFileData,
    dealValuationFileGroupData,
    dealValuationFileGroupList,
    dealValuationFileFile,
    saveFormDeps,
    onCreate,
    onCancel,
    onCloseModal,
    onCancelForm,
    onSubmitForm,
    onFileChange,
    onFileDelete,
    onFileViewClick,
    onSwitchModalEditMode,
  } = props;

  const [isLeaveConfirmModalOpen, setIsLeaveConfirmModalOpen]: [boolean, Function] = useState(false);

  const modalTitle: string = useMemo((): string => {
    if (dealValuationFileData && dealValuationFileData.file && dealValuationFileData.file.name) {
      return `${(isEditMode || isModalEditMode)
        ? `${MODAL_TITLE_EDIT_PREFIX} ` : ''}${dealValuationFileData.file.name}`;
    }

    return MODAL_TITLE_DEFAULT;
  }, [dealValuationFileData, isEditMode, isModalEditMode]);

  const dealValuationFileId: number = (dealValuationFileData && dealValuationFileData.id) || FormService.FORM_KEY_NEW;
  const dealValuationFileGroupId: number = (dealValuationFileGroupData && dealValuationFileGroupData.id)
    || FormService.FORM_KEY_NEW;
  const formKeyDealValuationFile: string = FormService.getIndexedFormFieldKey(dealValuationFileId);
  const formKeyDealValuationFileGroup: string = FormService.getIndexedFormFieldKey(dealValuationFileGroupId);
  const formPrefix: string = `dealData.dealValuationFilesData[${formKeyDealValuationFileGroup}].`
    + `files[${formKeyDealValuationFile}]`;
  const withEditModeSwitcher: boolean = !isNewEntity && !isNewDeal && !isEditMode;

  const menuItemDealValuationFileGroupList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemLabelKey: 'name',
    optionList: dealValuationFileGroupList,
  }), [dealValuationFileGroupList]);

  const onDealValuationFileFileChange: Function = useCallback((file: File): void => {
    if (onFileChange) {
      onFileChange(file, dealValuationFileGroupId, dealValuationFileId);
    }
  }, [dealValuationFileGroupId, dealValuationFileId, dealValuationFileFile]);

  const onDealValuationFileFileDelete: Function = useCallback((): void => {
    if (onFileDelete) {
      onFileDelete(dealValuationFileGroupId, dealValuationFileId);
    }
  }, [dealValuationFileGroupId, dealValuationFileId, dealValuationFileFile]);

  const cancelChangesAndCloseModal: MouseEventHandlerFunction = useCallback((): void => {
    if (onCloseModal) {
      onCloseModal();
    }
  }, []);

  const applyChangesAndCloseModal: MouseEventHandlerFunction = useCallback((): void => {
    if (isNewEntity && onCreate) {
      onCreate();
    } else if (onCloseModal) {
      onCloseModal();
    }
  }, [isNewEntity, ...(saveFormDeps || [])]);

  const closeModalByClickAway: Function = useCallback((): void => {
    if (!isSaveDisabled && isModalEditMode) {
      setIsLeaveConfirmModalOpen(true);
    } else {
      if (isModalEditMode && onCancel) {
        onCancel();
      }

      if (onCloseModal) {
        onCloseModal();
      }
    }
  }, [isSaveDisabled, isModalEditMode]);

  const closeLeaveConfirmModal: Function = useCallback((): void => {
    setIsLeaveConfirmModalOpen(false);
  }, []);

  const onLeaveConfirm: Function = useCallback((): void => {
    setIsLeaveConfirmModalOpen(false);

    if (onCancel) {
      onCancel();
    }

    if (onCloseModal) {
      onCloseModal();
    }
  }, []);

  const cancelForm: Function = useCallback((): void => {
    if (onCloseModal) {
      onCloseModal();
    }

    if (onCancelForm) {
      onCancelForm();
    }
  }, []);

  return (
    <ModalLayout
      isOpen={isOpen}
      isModalEditMode={isModalEditMode}
      withEditModeSwitcher={withEditModeSwitcher}
      title={modalTitle}
      editPermission={DEAL_VALUATION_FILE_EDIT}
      onCloseModal={cancelChangesAndCloseModal}
      onCloseModalByClickAway={closeModalByClickAway}
      onSwitchModalEditMode={onSwitchModalEditMode}
    >
      <Form>
        <Card isContainer withNoPadding>
          <Table>
            <TableBody isPlain withNoBorders withTwoColumns withHeaderColumn>
              <TableRow>
                <TableCell>
                  Group
                  {
                    (isEditMode || isNewDeal || isModalEditMode || isNewEntity) && (
                      <RequiredMark />
                    )
                  }
                </TableCell>
                <TableCell>
                  {
                    (isEditMode || isNewDeal || isModalEditMode || isNewEntity) ? (
                      <FormFieldSelect
                        isBordered
                        withBlankElement
                        isDisabled={(!isNewEntity && dealValuationFileData && !dealValuationFileData.file)
                          || (dealValuationFileGroupList && !dealValuationFileGroupList.length)}
                        name={`${formPrefix}.groupId`}
                        optionsTotal={dealValuationFileGroupList && dealValuationFileGroupList.length}
                      >
                        {menuItemDealValuationFileGroupList}
                      </FormFieldSelect>
                    ) : (dealValuationFileGroupData && dealValuationFileGroupData.name)
                  }
                </TableCell>
              </TableRow>
              {
                (isEditMode || isNewDeal || isModalEditMode || isNewEntity) && (
                  <TableRow>
                    <TableCell>
                      Name
                      <RequiredMark />
                    </TableCell>
                    <TableCell>
                      <FormFieldText isBordered name={`${formPrefix}.name`} />
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isNewDeal || isNewEntity || (!isEditMode && !isModalEditMode
                  && !isNewDeal && !isNewEntity)) && (
                  <TableRow>
                    <TableCell>
                      File
                      {
                        (isNewDeal || isModalEditMode || isNewEntity) && (
                          <RequiredMark />
                        )
                      }
                    </TableCell>
                    <TableCell>
                      {
                        (isNewDeal || isModalEditMode || isNewEntity) ? (
                          <FormFieldUpload
                            isDroppable
                            withAllFilesAllowed
                            uploadFile={dealValuationFileFile}
                            name={`${formPrefix}.file`}
                            buttonLabel="Upload New File"
                            onFileChange={onDealValuationFileFileChange}
                            onFileDelete={onDealValuationFileFileDelete}
                          />
                        ) : (
                          dealValuationFileData && dealValuationFileData.file && (
                            <div>
                              <FilteredLink
                                isExternal
                                isDownloadLink
                                link={dealValuationFileData.file.link}
                                onMouseDown={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                  onFileViewClick(event, dealValuationFileData.file, dealValuationFileGroupData.name);
                                }}
                              >
                                <DownloadIcon />
                                {' '}
                                Download
                              </FilteredLink>
                            </div>
                          )
                        )
                      }
                    </TableCell>
                  </TableRow>
                )
              }
            </TableBody>
          </Table>
        </Card>
        {
          (isModalEditMode || isNewEntity) ? (
            <div>
              {
                isSaving && (
                  <Loader isInline isFloating isModal isSubmit />
                )
              }
              <ControlButtonBlock
                isModal
                isModalEditMode={isModalEditMode}
                isNewEntity={isNewDeal || isNewEntity}
                isSaveDisabled={isSaveDisabled}
                withLoader={isSaving}
                withOffset={isRightDrawerOpen}
                onCancelForm={cancelForm}
                onSubmitForm={onSubmitForm}
              />
            </div>
          ) : (
            <ModalButtonBlock>
              <Button onClick={applyChangesAndCloseModal}>
                Close
              </Button>
            </ModalButtonBlock>
          )
        }
      </Form>
      <LeaveConfirmModal
        isSubModal
        isOpen={isLeaveConfirmModalOpen}
        onConfirm={onLeaveConfirm}
        onCloseModal={closeLeaveConfirmModal}
      />
    </ModalLayout>
  );
});

export default DealValuationFileModal;
